import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch } from "hooks/useAppDispatch";
import { uploadFile } from "reduxStore/files/thunks";
import { Messages, NotificationSeverity } from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";
import { getNewEntitiesIds } from "utils/FilterEntities";
import { Organization } from "types/organization";
import { Tag } from "types/tag";
import { ICreateOrUpdateUserCredentials } from "types/auth";
import { ROLES, User } from "types/user";
import {
  useAddMentoringMutation,
  useAddUserTagsMutation,
  useDeleteUserTagByTagAndUserMutation,
  useGetOrganizationsQuery,
  useGetUserByIdQuery,
  useGetUsersRoleByOrgLazyQuery,
  useDeleteMentoringMutation,
  useUpdateUserMutation,
} from "graphql/__generated__/hooks";
import { updateUserThunk } from "reduxStore/auth/thunks";

export const useUserEdit = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { notify } = useNotifications();

  // avatar/image state
  const [errorImage, setErrorImage] = useState("");
  const [avatar, setAvatar] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // tags state
  const [tags, setTags] = useState<Tag[]>([]);
  const [originalTags, setOriginalTags] = useState<Tag[]>([]);

  // organization state
  const { data: dataOrganization } = useGetOrganizationsQuery();
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const organizations = useMemo(
    () => dataOrganization?.organization || [],
    [dataOrganization],
  );

  // mentors state
  const [selectedCoaches, setSelectedCoaches] = useState<User[]>([]);
  const [getUsersRoleByOrg, { data: usersRoleByOrgData }] =
    useGetUsersRoleByOrgLazyQuery();
  const coaches = useMemo(
    () => usersRoleByOrgData?.organization_user.map((ou) => ou.user) || [],
    [usersRoleByOrgData],
  );
  // keep original mentorings to diff on save
  const [originalMentorings, setOriginalMentorings] = useState<
    { mentorId: string; mentoringId: string }[]
  >([]);

  // user data
  const { data: userData, loading: userLoading } = useGetUserByIdQuery({
    variables: { userId: userId ?? "" },
    skip: !userId,
  });
  const userToEdit = userData?.user_by_pk || null;

  // mutations
  const [updateUser] = useUpdateUserMutation();
  const [addUserTags] = useAddUserTagsMutation();
  const [deleteUserTag] = useDeleteUserTagByTagAndUserMutation();
  const [addMentoring] = useAddMentoringMutation();
  const [deleteMentoring] = useDeleteMentoringMutation();

  // initialize from user
  useEffect(() => {
    if (userToEdit) {
      setAvatar(userToEdit.picture_url ?? "");
      if (userToEdit.tags) {
        const currentTags: Tag[] = [];
        userToEdit.tags.forEach((tagItem) => {
          if (tagItem.tag) {
            currentTags.push({
              tag_id: tagItem.tag.tag_id,
              name: tagItem.tag.name,
              type: tagItem.tag.type,
            });
          }
        });
        setTags(currentTags);
        setOriginalTags(currentTags);
      }
    }
  }, [userToEdit]);

  // preload mentors if student
  useEffect(() => {
    if (userToEdit?.role === ROLES.STUDENT && userData?.mentoring) {
      const mentors = (userData.mentoring || []).map((m) => m.mentor) as User[];
      setSelectedCoaches(mentors);
      const mapping =
        (userData.mentoring || []).map((m) => ({
          mentorId: m.mentor?.user_id as string,
          mentoringId: m.mentoring_id as string,
        })) || [];
      setOriginalMentorings(mapping);
    }
  }, [userToEdit?.role, userData?.mentoring]);

  // preselect organization
  useEffect(() => {
    if (
      userToEdit &&
      "org_id" in userToEdit &&
      userToEdit.org_id &&
      organizations.length > 0
    ) {
      const selectedOrganizationData = organizations.find(
        (org) => org.org_id === userToEdit.org_id,
      );
      selectedOrganizationData &&
        setSelectedOrganization(selectedOrganizationData as Organization);
    }
  }, [userToEdit, organizations]);

  // load coaches when organization changes
  useEffect(() => {
    if (selectedOrganization?.org_id) {
      getUsersRoleByOrg({
        variables: { orgId: selectedOrganization.org_id, role: ROLES.COACH },
      });
    }
  }, [selectedOrganization, getUsersRoleByOrg]);

  // image upload handlers
  const onUploadFile = (event: React.BaseSyntheticEvent) => {
    const file = event.target.files[0];
    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      setErrorImage("Invalid file type: only PNG and JPEG are allowed");
      return;
    }
    if (file.size > 1024 * 1024 * 10) {
      setErrorImage("File size is too large: max size is 10MB");
      return;
    }
    if (errorImage !== "") {
      setErrorImage("");
    }
    setAvatar(URL.createObjectURL(file));
    setAvatarFile(file);
  };

  const UploadFile = async () => {
    if (avatarFile) {
      setUploading(true);
      const result = await dispatch(uploadFile(avatarFile));
      if (result.meta.requestStatus === "rejected") {
        setErrorImage(Messages.errorImageUpload);
        return "ERROR";
      }
      setUploading(false);
      return result?.payload?.filePath;
    } else {
      return null;
    }
  };

  // tag helpers
  const handleAddTag = async (tag: Tag) => {
    setTags((prev) => [...prev, tag]);
  };
  const handleDeleteTag = async (tagToRemove: Tag) => {
    setTags((prev) => prev.filter((t) => t.tag_id !== tagToRemove.tag_id));
  };

  const editTags = async (uId: string) => {
    const newTagsIds = getNewEntitiesIds(originalTags, tags, "tag_id") as
      | string[]
      | undefined;
    const newTagsData =
      (newTagsIds || []).map((tagId) => ({
        user_id: uId,
        tag_id: tagId,
      })) || [];
    if (newTagsData.length > 0) {
      await addUserTags({ variables: { objects: newTagsData } });
    }

    const removedRequirementTagsIds = getNewEntitiesIds(
      tags,
      originalTags,
      "tag_id",
    ) as string[] | undefined;
    if (removedRequirementTagsIds && removedRequirementTagsIds.length > 0) {
      for (const tagId of removedRequirementTagsIds) {
        await deleteUserTag({ variables: { tagId, userId: uId } });
      }
    }
  };

  const [loading, setLoading] = useState(false);
  const handleEditUser = async (
    reqValues: ICreateOrUpdateUserCredentials,
    picture_url: string | null,
  ) => {
    setLoading(true);
    const { payload } = await dispatch(updateUserThunk(reqValues));
    if (payload === "success") {
      if (picture_url) reqValues.picture_url = picture_url;
      const orgId = selectedOrganization?.org_id ?? null;
      let data = { ...reqValues, org_id: orgId };
      await updateUser({
        variables: { id: reqValues.user_id, user: data as User },
      });
      // diff mentorings
      if (userId) {
        const menteeId = userId as string;
        const originalIds = new Set(originalMentorings.map((m) => m.mentorId));
        const selectedIds = new Set(
          (selectedCoaches.map((c) => c.user_id).filter(Boolean) as string[]) ||
            [],
        );
        // additions
        for (const coach of selectedCoaches) {
          const mentorId = coach.user_id || "";
          if (mentorId && !originalIds.has(mentorId)) {
            await addMentoring({
              variables: { mentee: menteeId, mentor: mentorId },
            });
          }
        }
        // removals
        for (const { mentorId, mentoringId } of originalMentorings) {
          if (!selectedIds.has(mentorId)) {
            await deleteMentoring({ variables: { id: mentoringId } });
          }
        }
      }
      if (userId) {
        await editTags(userId);
      }
      notify({
        message: Messages.successUserUpdated,
        severity: NotificationSeverity.success,
      });
      navigate("/users");
    } else {
      notify({
        message: Messages.errorUserUpdated,
        severity: NotificationSeverity.error,
      });
    }
    setLoading(false);
  };

  return {
    // loading and user
    userLoading,
    userToEdit,
    loading,

    // avatar
    avatar,
    errorImage,
    uploading,
    onUploadFile,
    UploadFile,

    // tags
    tags,
    handleAddTag,
    handleDeleteTag,

    // organization
    organizations,
    selectedOrganization,
    setSelectedOrganization,

    // mentors
    selectedCoaches,
    setSelectedCoaches,
    coaches,

    // actions
    handleEditUser,

    // tags
    editTags,
  };
};
